import React from 'react';
import {
  TextField,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import FormButtons from '../../../../../app/components/FormButtons';
import ListTitleBox from '../../../../../app/components/list/ListTitleBox';
import { TableFormData, TableStatus } from '../../types';
import { tableValidationSchema } from './validation';

interface TableFormProps {
  onSubmit: (
    data: TableFormData,
    setError: UseFormSetError<TableFormData>,
    tableId?: string
  ) => Promise<boolean>;
  defaultValues: TableFormData & { restaurantId: string; tableId?: string };
  loading: boolean;
  onCancel: () => void;
  submitLabel: string;
  tableId?: string;
}

const TableForm: React.FC<TableFormProps> = ({
  onSubmit,
  defaultValues,
  loading,
  tableId,
  onCancel,
  submitLabel,
}) => {
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<TableFormData>({
    resolver: yupResolver(tableValidationSchema()),
    defaultValues,
  });

  const handleSubmitForm = async (data: TableFormData) => {
    const success = await onSubmit(data, setError, tableId);
    if (success) {
      toast.success(
        defaultValues.tableId
          ? t('table.update.success')
          : t('table.create.success')
      );
      onCancel();
    }
  };

  return (
    <>
      <ListTitleBox
        title={
          defaultValues.tableId
            ? t('table.form.edit.title')
            : t('table.form.create.title')
        }
        onBackClick={onCancel}
      />
      <Paper sx={{ p: 4 }}>
        <form onSubmit={handleSubmit(handleSubmitForm)}>
          <TextField
            {...register('tableNumber')}
            label={t('table.form.fields.number')}
            type="number"
            inputProps={{ min: 1 }}
            error={!!errors.tableNumber}
            helperText={errors.tableNumber?.message}
            fullWidth
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth sx={{ mb: 2 }} error={!!errors.status}>
            <InputLabel>{t('table.form.fields.status')}</InputLabel>
            <Select
              {...register('status')}
              label={t('table.form.fields.status')}
              value={defaultValues.status}
            >
              <MenuItem value={TableStatus.Available}>
                {t('table.status.available')}
              </MenuItem>
              <MenuItem value={TableStatus.Occupied}>
                {t('table.status.occupied')}
              </MenuItem>
              <MenuItem value={TableStatus.Reserved}>
                {t('table.status.reserved')}
              </MenuItem>
            </Select>
          </FormControl>

          <TextField
            {...register('capacity')}
            label={t('table.form.fields.capacity')}
            type="number"
            error={!!errors.capacity}
            helperText={errors.capacity?.message}
            fullWidth
            sx={{ mb: 2 }}
          />

          <TextField
            {...register('notes')}
            label={t('table.form.fields.notes')}
            error={!!errors.notes}
            helperText={errors.notes?.message}
            fullWidth
            multiline
            rows={3}
            sx={{ mb: 2 }}
          />

          <FormButtons
            loading={loading}
            cancelEdit={onCancel}
            submitText={submitLabel}
          />
        </form>
      </Paper>
    </>
  );
};

export default TableForm;
